import { locationOwner } from './location-owner.util';
const SEPARATOR = ' - ';

/**
 * Splits a location dropdown label into its place name and its city.
 *
 * Every location option the app lists ends with the city, joined by ' - ': `LocationModel`'s own
 * `dropDownDisplayName` builds `title - city`, and the bulk locations endpoint sends the same shape
 * prefixed with the owning customer (`customer - title - city`). Reading the city off the label is
 * what lets a caller skip a `getLocation(id)` round trip just to name the city.
 *
 * Split on the LAST separator, never the first — the city is a single trailing segment, while the
 * part before it can hold its own ' - ' (a customer prefix, or a title such as "Riyadh - North").
 * A label with no separator is all name and no city, which is how a location saved without a city
 * comes back.
 */
export function splitLocationLabel(label: string): { title: string; city: string } {
  const at = (label || '').lastIndexOf(SEPARATOR);
  if (at === -1) {
    return { title: label || '', city: '' };
  }
  return { title: label.slice(0, at), city: label.slice(at + SEPARATOR.length) };
}

/**
 * Drops the owning customer from a bulk locations label.
 *
 * `BulkLocation.dropDownDisplayName` hands back `value` untouched, and that value arrives as
 * `customer - title - city` — the same shape `bulk-shipments-grid` strips for its route cell
 * (`bulk-shipments-grid.component.ts:185`). Match the customer name exactly instead of counting
 * segments: a customer named "ABC - Logistics" holds the separator itself, so
 * `"ABC - Logistics - man loc - Cairo"` has to yield `"man loc - Cairo"`.
 *
 * An option that carries no `customerName` is either not a bulk location or one patched in through
 * `modelToLookup`, and is already prefix-free.
 */
function stripCustomerPrefix(location: any): string {
  const label = location.value || location.dropDownDisplayName || '';
  // Through `locationOwner` so the label names the same owner the payload sends. The endpoint
  // fills one side and nulls the other, so the two could not actually disagree — but choosing an
  // order here independently would make that a coincidence rather than a guarantee.
  const owner = locationOwner(location)?.name;
  const prefix = owner ? `${owner}${SEPARATOR}` : '';
  return prefix && label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

/**
 * Names a picked location option, whichever shape it arrived in.
 *
 * A location picked from a dropdown is a `Lookup` carrying only the label, so it goes through
 * `splitLocationLabel`. One that was just created in the add-location dialog is the model itself,
 * labelled with its address rather than `title - city` — there the object's own fields are both
 * available and more reliable than anything a split could recover.
 */
export function locationLabelParts(location: any): { title: string; city: string } {
  if (!location) {
    return { title: '', city: '' };
  }
  if (location.city?.name) {
    return { title: location.title || location.value || '', city: location.city.name };
  }
  const label = stripCustomerPrefix(location);
  // `cityId` settles what a split can only guess at. A location saved without a city gets no city
  // appended by `dropDownDisplayName`, so every separator left in the label belongs to the title —
  // "Riyadh - North" is one name, not a name and a city. Only trust the field when the option
  // actually carries it: `undefined` is an option shape that never had a `cityId`, not a location
  // that has no city, and splitting stays the safer guess there.
  if (location.cityId !== undefined && !location.cityId) {
    return { title: label, city: '' };
  }
  return splitLocationLabel(label);
}
